/**
 * CV Shade Cross-Sell
 * Manual picks and collection fill candidates render server-side; shade matches load
 * via search Section Rendering API.
 * Configured products fill in order: manual picks, Cross Sell View All Collection,
 * Fast Simon ranked matches within the product's own category, then shade search.
 */
(function shadeCrossSell() {
  'use strict';

  const SEARCH_SECTION_ID = 'cv-shade-cross-sell';

  const SELECTORS = {
    block: '.cv-shop-by-shade-cross-sell[data-cross-sell-mode]',
    productsSlot: '[data-cv-shade-cross-sell-products]',
    collectionPool: '[data-cv-shade-cross-sell-collection]',
    card: '.cv-shade-cross-sell-card',
    apiList: '.cv-shade-cross-sell__list',
  };

  const CLASSES = {
    desktopHidden: 'cv-shade-cross-sell-card--desktop-hidden',
    mobileHidden: 'cv-shade-cross-sell-card--mobile-hidden',
    gridItem: 'grid__item',
    previewVisible: 'cv-shade-cross-sell-card__shell--preview',
  };

  const FAST_SIMON_ENDPOINT = 'https://api.fastsimon.com/categories_navigation';
  const FAST_SIMON_TIMEOUT = 2000;
  const SECTION_TIMEOUT = 4000;
  const HANDLE_PATTERN = /^[a-z0-9][a-z0-9_-]*$/i;

  const searchCache = new Map();
  const fastSimonCache = new Map();

  const COLLECTION_FILTER = {
    broadPrefixes: ['all-', 'color-tones-', 'shop-by-'],
    broadHandles: new Set([
      'all',
      'frontpage',
      'all-products',
      'popular-colors',
      'new-arrivals',
      'new-arrivals-2',
      'deals-of-the-week',
      'best-sellers',
      'sale',
      'clearance',
    ]),
  };

  /* --- Parsing --- */

  function parseHandleList(value) {
    if (!value) return [];

    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map(String).filter(Boolean);
      }
    } catch (error) {
      /* fall through to comma-separated list */
    }

    return String(value)
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
  }

  function parseLimit(value, fallback) {
    const limit = Number.parseInt(value, 10);
    return Number.isFinite(limit) && limit > 0 ? limit : fallback;
  }

  function getCardHandle(card) {
    return card.dataset.productHandle || '';
  }

  /* --- Collection filtering --- */

  function isBroadCollection(handle, ignoredHandles) {
    if (!handle) return true;
    if (ignoredHandles.includes(handle)) return true;
    if (COLLECTION_FILTER.broadHandles.has(handle)) return true;

    return COLLECTION_FILTER.broadPrefixes.some((prefix) => handle.startsWith(prefix));
  }

  function categoryCollections(handles, ignoredHandles) {
    return handles.filter((handle) => !isBroadCollection(handle, ignoredHandles));
  }

  function filterCards(cards, root, applySharedExclusion, excludedHandles = new Set()) {
    const excludeHandle = root.dataset.excludeHandle;
    const ignoredHandles = parseHandleList(root.dataset.ignoreCollections);
    const excludedCollections = parseHandleList(root.dataset.excludedCollections);
    const productCategories = categoryCollections(
      parseHandleList(root.dataset.productCollections),
      ignoredHandles
    );
    const checkShared =
      applySharedExclusion && root.dataset.excludeSharedCollections === 'true';

    return cards.filter((card) => {
      const handle = getCardHandle(card);

      if (handle && excludedHandles.has(handle)) {
        return false;
      }

      if (excludeHandle && handle === excludeHandle) {
        return false;
      }

      if (
        root.dataset.excludeOutOfStock === 'true' &&
        card.dataset.productAvailable !== 'true'
      ) {
        return false;
      }

      const cardHandles = parseHandleList(card.dataset.collectionHandles);

      if (cardHandles.some((collectionHandle) => excludedCollections.includes(collectionHandle))) {
        return false;
      }

      if (!checkShared) {
        return true;
      }

      const cardCategories = categoryCollections(cardHandles, ignoredHandles);

      if (!productCategories.length || !cardCategories.length) {
        return true;
      }

      return !productCategories.some((collectionHandle) => cardCategories.includes(collectionHandle));
    });
  }

  function filterManualCards(cards, root) {
    const excludeHandle = root.dataset.excludeHandle;

    return cards.filter((card) => {
      if (excludeHandle && getCardHandle(card) === excludeHandle) {
        return false;
      }

      return true;
    });
  }

  function shuffle(items) {
    const list = [...items];

    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }

    return list;
  }

  /* --- Limits & visibility --- */

  function getDesktopLimit(root) {
    return parseLimit(root.dataset.productLimit, 3);
  }

  function getMobileLimit(root) {
    return parseLimit(root.dataset.productLimitMobile, getDesktopLimit(root));
  }

  function getFetchLimit(root) {
    return Math.max(getDesktopLimit(root), getMobileLimit(root));
  }

  function applyResponsiveVisibility(root) {
    const desktopLimit = getDesktopLimit(root);
    const mobileLimit = getMobileLimit(root);

    root.querySelectorAll(SELECTORS.card).forEach((card, index) => {
      card.classList.toggle(CLASSES.desktopHidden, index >= desktopLimit);
      card.classList.toggle(CLASSES.mobileHidden, index >= mobileLimit);
    });
  }

  function hasProducts(root) {
    return root.querySelectorAll(SELECTORS.card).length > 0;
  }

  function showBlock(root) {
    root.classList.remove('is-pending', 'is-loading', 'is-empty');
  }

  function hideBlock(root) {
    root.classList.remove('is-pending', 'is-loading');
    root.classList.add('is-empty');
  }

  /* --- Product selection --- */

  function selectShadeCards(list, root, excludedHandles, maxCount) {
    if (!list || maxCount <= 0) return [];

    const allCards = [...list.querySelectorAll(SELECTORS.card)];
    if (!allCards.length) return [];

    let cards = filterCards(allCards, root, true, excludedHandles);
    if (!cards.length) {
      cards = filterCards(allCards, root, false, excludedHandles);
    }

    if (root.dataset.randomizeProducts !== 'false') {
      cards = shuffle(cards);
    }

    return cards.slice(0, maxCount);
  }

  function selectCards(list, root) {
    if (!list) return null;

    const cards = selectShadeCards(list, root, new Set(), getFetchLimit(root));
    if (!cards.length) return null;

    list.innerHTML = '';
    cards.forEach((card) => list.appendChild(card));

    return list;
  }

  function getManualCards(root) {
    const slot = root.querySelector(SELECTORS.productsSlot);
    const sourceList = slot?.querySelector(SELECTORS.apiList);

    if (!sourceList) return [];

    return filterManualCards([...sourceList.querySelectorAll(SELECTORS.card)], root);
  }

  function getCollectionPoolCards(root) {
    const pool = root.querySelector(SELECTORS.collectionPool);

    if (!pool) return [];

    return filterManualCards([...pool.querySelectorAll(SELECTORS.card)], root);
  }

  /* --- DOM rendering --- */

  function activateImages(container) {
    if (!container) return;

    container.querySelectorAll('img[loading="lazy"]').forEach((img) => {
      img.loading = 'eager';
    });

    container.querySelectorAll('img[data-src]').forEach((img) => {
      img.src = img.dataset.src;
    });
  }

  function prepareGridCard(card) {
    card.classList.add(CLASSES.gridItem);
    card.querySelectorAll('.card--horizontal').forEach((el) => {
      el.classList.remove('card--horizontal');
    });
  }

  function prepareHorizontalListCard(card) {
    card.classList.remove(CLASSES.gridItem);
    card.querySelectorAll('.card').forEach((el) => {
      el.classList.add('card--horizontal');
    });
  }

  function buildProductGrid(root, cards) {
    const grid = document.createElement('ul');

    grid.className = 'cv-shade-cross-sell__grid list-unstyled';
    grid.setAttribute('role', 'list');

    cards.forEach((card) => {
      prepareGridCard(card);
      grid.appendChild(card);
    });

    return grid;
  }

  function buildHorizontalList(cards) {
    const list = document.createElement('ul');

    list.className = 'cv-shade-cross-sell__list cv-shade-cross-sell__list--horizontal list-unstyled';
    list.setAttribute('role', 'list');

    cards.forEach((card) => {
      prepareHorizontalListCard(card);
      list.appendChild(card);
    });

    return list;
  }

  function mountProducts(slot, root, cards) {
    slot.innerHTML = '';

    const layout = root.dataset.cardLayout || 'grid';

    if (layout === 'horizontal_list') {
      slot.appendChild(buildHorizontalList(cards));
    } else {
      slot.appendChild(buildProductGrid(root, cards));
    }

    applyResponsiveVisibility(root);
    activateImages(slot);
  }

  /* --- API --- */

  function shopRoot() {
    return window.Shopify?.routes?.root || '/';
  }

  function fetchWithTimeout(url, options, timeout) {
    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), timeout);

    return fetch(url, { ...options, signal: controller.signal }).finally(() => {
      window.clearTimeout(timer);
    });
  }

  function cachedJson(cache, url, options, timeout) {
    if (!cache.has(url)) {
      cache.set(
        url,
        fetchWithTimeout(url, options, timeout)
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Request failed: ${response.status}`);
            }

            return response.json();
          })
          .catch((error) => {
            cache.delete(url);
            throw error;
          })
      );
    }

    return cache.get(url);
  }

  function buildSearchUrl(shadeTag) {
    const query = encodeURIComponent(`tag:"${shadeTag}"`);
    const sections = encodeURIComponent(SEARCH_SECTION_ID);

    return `${shopRoot()}search?q=${query}&type=product&sections=${sections}`;
  }

  function buildFastSimonUrl(root, limit) {
    const facet = root.dataset.fastSimonFacet || 'Shades';
    const narrow = JSON.stringify([
      [facet, root.dataset.shadeTag],
      ['Type', root.dataset.productType],
      ['Isp-in-stock', 'True'],
    ]);

    const params = new URLSearchParams({
      UUID: root.dataset.fastSimonUuid,
      store_id: root.dataset.fastSimonStoreId,
      api_type: 'json',
      category_id: root.dataset.fastSimonCategoryId,
      narrow: narrow,
      products_per_page: String(limit),
      page_num: '1',
    });

    return `${FAST_SIMON_ENDPOINT}?${params.toString()}`;
  }

  async function fetchRankedHandles(root, limit) {
    if (!root.dataset.fastSimonUuid || !root.dataset.fastSimonStoreId) return [];
    if (!root.dataset.fastSimonCategoryId || !root.dataset.productType) return [];
    if (!root.dataset.shadeTag) return [];

    const url = buildFastSimonUrl(root, limit);
    const payload = await cachedJson(fastSimonCache, url, {}, FAST_SIMON_TIMEOUT);

    return (payload?.items || [])
      .map((item) => String(item.u || '').split('/products/')[1] || '')
      .map((handle) => handle.split('?')[0].replace(/\/$/, ''))
      .filter(Boolean);
  }

  async function fetchCardByHandle(handle) {
    if (!HANDLE_PATTERN.test(handle)) return null;

    const url = `${shopRoot()}products/${encodeURIComponent(handle)}?sections=${encodeURIComponent(
      SEARCH_SECTION_ID
    )}`;

    const payload = await fetchWithTimeout(url, { credentials: 'same-origin' }, SECTION_TIMEOUT)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Card request failed: ${response.status}`);
        }

        return response.json();
      });

    const html = payload[SEARCH_SECTION_ID] || '';

    if (!html.trim()) return null;

    const { list } = parseSearchSection(html);

    return list ? list.querySelector(SELECTORS.card) : null;
  }

  async function fetchRankedFillCards(root, excludedHandles, maxCount) {
    if (maxCount <= 0) return [];

    try {
      const handles = await fetchRankedHandles(root, maxCount + excludedHandles.size + 6);
      const wanted = handles
        .filter((handle) => !excludedHandles.has(handle))
        .slice(0, maxCount + 2);

      if (!wanted.length) return [];

      const cards = await Promise.all(
        wanted.map((handle) => fetchCardByHandle(handle).catch(() => null))
      );

      return filterManualCards(cards.filter(Boolean), root).slice(0, maxCount);
    } catch (error) {
      console.error('[cv-shade-cross-sell]', error);
      return [];
    }
  }

  function parseSearchSection(html) {
    const template = document.createElement('template');
    template.innerHTML = html.trim();

    return {
      list: template.content.querySelector(SELECTORS.apiList),
    };
  }

  async function fetchSearchPayload(shadeTag) {
    const url = buildSearchUrl(shadeTag);

    return cachedJson(searchCache, url, { credentials: 'same-origin' }, SECTION_TIMEOUT);
  }

  async function fetchShadeBackfillCards(root, excludedHandles, maxCount) {
    const shadeTag = root.dataset.shadeTag;

    if (!shadeTag || maxCount <= 0) return [];

    try {
      const payload = await fetchSearchPayload(shadeTag);
      const html = payload[SEARCH_SECTION_ID] || '';
      if (!html.trim()) return [];

      const { list } = parseSearchSection(html);
      return selectShadeCards(list, root, excludedHandles, maxCount);
    } catch (error) {
      console.error('[cv-shade-cross-sell]', error);
      return [];
    }
  }

  async function fetchProducts(root) {
    const shadeTag = root.dataset.shadeTag;
    const slot = root.querySelector(SELECTORS.productsSlot);

    if (!shadeTag || !slot) return false;

    try {
      const payload = await fetchSearchPayload(shadeTag);
      const html = payload[SEARCH_SECTION_ID] || '';
      if (!html.trim()) return false;

      const { list } = parseSearchSection(html);
      const selectedList = selectCards(list, root);
      if (!selectedList) return false;

      mountProducts(slot, root, [...selectedList.querySelectorAll(SELECTORS.card)]);

      return hasProducts(root);
    } catch (error) {
      console.error('[cv-shade-cross-sell]', error);
      return false;
    }
  }

  /* --- Init --- */

  function initConfiguredBlock(root) {
    const slot = root.querySelector(SELECTORS.productsSlot);
    const cards = getManualCards(root).slice(0, getFetchLimit(root));

    if (!cards.length) {
      hideBlock(root);
      return;
    }

    mountProducts(slot, root, cards);
    showBlock(root);
  }

  async function initShadeBlock(root) {
    const fetchLimit = getFetchLimit(root);
    const existingCount = root.querySelectorAll(SELECTORS.card).length;

    if (existingCount >= fetchLimit) {
      applyResponsiveVisibility(root);
      showBlock(root);
      return;
    }

    root.classList.add('is-loading');

    const loaded = await fetchProducts(root);
    root.classList.remove('is-loading');

    if (loaded && hasProducts(root)) {
      showBlock(root);
    } else {
      hideBlock(root);
    }
  }

  function bindPreviewHover(root) {
    if (root.dataset.previewHoverBound === 'true') {
      return;
    }

    root.dataset.previewHoverBound = 'true';

    let active = null;

    function clearPreview() {
      if (!active) {
        return;
      }

      active.classList.remove(CLASSES.previewVisible);
      active = null;
    }

    function isOverMedia(shell, x, y) {
      const media = shell.querySelector('.card__inner');

      if (!media) {
        return false;
      }

      const box = media.getBoundingClientRect();

      return x >= box.left && x <= box.right && y >= box.top && y <= box.bottom;
    }

    root.addEventListener('pointermove', (event) => {
      if (event.pointerType !== 'mouse') {
        clearPreview();
        return;
      }

      const shell = event.target.closest('.cv-shade-cross-sell-card__shell');

      if (!shell || !isOverMedia(shell, event.clientX, event.clientY)) {
        clearPreview();
        return;
      }

      if (active !== shell) {
        clearPreview();
        shell.classList.add(CLASSES.previewVisible);
        active = shell;
      }
    });

    root.addEventListener('pointerleave', clearPreview);
  }

  async function initBlock(root) {
    const mode = root.dataset.crossSellMode;

    bindPreviewHover(root);

    if (mode === 'manual') {
      initConfiguredBlock(root);
      return;
    }

    await initShadeBlock(root);
  }

  async function init() {
    const blocks = document.querySelectorAll(SELECTORS.block);

    for (const root of blocks) {
      await initBlock(root);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
